import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Loader2, Users, TrendingUp, Eye, ArrowRight, AlertTriangle, Check, X, RefreshCw, Settings, Plus, Copy, Trash2, Send, Link, Megaphone, ExternalLink } from 'lucide-react';
import { API_URL } from './shared';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';

const ReferralsTab = ({ token }) => {
  const [analytics, setAnalytics] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [fraud, setFraud] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [configForm, setConfigForm] = useState({ enabled: true, reward_expiry_days: 30, cookie_expiry_days: 30 });
  const [savingConfig, setSavingConfig] = useState(false);

  // Campaign codes state
  const [campaigns, setCampaigns] = useState([]);
  const [showNewCampaign, setShowNewCampaign] = useState(false);
  const [newCampaign, setNewCampaign] = useState({ code: '', name: '', source: 'social', description: '' });
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [campaignLinks, setCampaignLinks] = useState(null);

  // Email blast state
  const [sendingBlast, setSendingBlast] = useState(false);
  const [blastRecipientType, setBlastRecipientType] = useState('all');

  // Name referrals state
  const [nameReferrals, setNameReferrals] = useState([]);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [analyticsRes, fraudRes] = await Promise.all([
        axios.get(`${API_URL}/admin/referrals/analytics`, { headers }),
        axios.get(`${API_URL}/admin/referrals/fraud`, { headers }),
      ]);
      setAnalytics(analyticsRes.data);
      setFraud(fraudRes.data);
      if (analyticsRes.data.config) {
        setConfigForm({
          enabled: analyticsRes.data.config.enabled !== false,
          reward_expiry_days: analyticsRes.data.config.reward_expiry_days || 30,
          cookie_expiry_days: analyticsRes.data.config.cookie_expiry_days || 30,
        });
      }
    } catch (error) {
      toast.error('Failed to load referral analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchReferrals = async (status) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const statusParam = status && status !== 'ALL' ? `?status=${status}` : '';
      const res = await axios.get(`${API_URL}/admin/referrals/list${statusParam}`, { headers });
      setReferrals(res.data);
    } catch (error) {
      toast.error('Failed to load referrals');
    }
  };

  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/referrals/campaigns`, { headers: { Authorization: `Bearer ${token}` } });
      setCampaigns(res.data);
    } catch (error) {
      toast.error('Failed to load campaigns');
    }
  };

  const fetchNameReferrals = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/referrals/name-referrals`, { headers: { Authorization: `Bearer ${token}` } });
      setNameReferrals(res.data);
    } catch (error) {
      toast.error('Failed to load name referrals');
    }
  };

  const invalidateReferral = async (id) => {
    if (!window.confirm('Invalidate this referral?')) return;
    try {
      await axios.post(`${API_URL}/admin/referrals/${id}/invalidate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Referral invalidated');
      fetchReferrals(statusFilter);
    } catch (error) {
      toast.error('Failed to invalidate');
    }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.put(`${API_URL}/admin/referrals/config`, configForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Campaign settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingConfig(false);
    }
  };

  const createCampaign = async () => {
    if (!newCampaign.code.trim()) {
      toast.error('Campaign code is required');
      return;
    }
    setCreatingCampaign(true);
    try {
      const res = await axios.post(`${API_URL}/admin/referrals/campaigns`, newCampaign, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Campaign created!');
      // Build links using current domain (not backend FRONTEND_URL)
      const origin = window.location.origin;
      const code = res.data.campaign.code;
      const base = `${origin}/join?ref=${code}`;
      setCampaignLinks({
        links: {
          direct: base,
          facebook: `${base}&utm_source=facebook`,
          instagram: `${base}&utm_source=instagram`,
          email: `${base}&utm_source=email`,
          whatsapp: `${base}&utm_source=whatsapp`,
          tiktok: `${base}&utm_source=tiktok`,
        }
      });
      setNewCampaign({ code: '', name: '', source: 'social', description: '' });
      setShowNewCampaign(false);
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create campaign');
    } finally {
      setCreatingCampaign(false);
    }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm('Delete this campaign code?')) return;
    try {
      await axios.delete(`${API_URL}/admin/referrals/campaigns/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  const sendReferralBlast = async () => {
    if (!window.confirm(`Send referral program email to ${blastRecipientType === 'all' ? 'ALL' : blastRecipientType} users?`)) return;
    setSendingBlast(true);
    try {
      const res = await axios.post(`${API_URL}/admin/referrals/send-program-email`, { recipient_type: blastRecipientType }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Referral program email sent to ${res.data.sent} users (${res.data.failed} failed)`);
    } catch (error) {
      toast.error('Failed to send emails');
    } finally {
      setSendingBlast(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#0A2342]" /></div>;
  }

  const kpis = analytics?.kpis || {};
  const funnel = analytics?.funnel || {};

  const sections = [
    { id: 'overview', label: 'Overview' },
    { id: 'campaigns', label: 'Campaigns' },
    { id: 'referrals', label: 'Referrals' },
    { id: 'top-referrers', label: 'Top Referrers' },
    { id: 'name-referrals', label: 'By Name' },
    { id: 'fraud', label: 'Fraud' },
    { id: 'email-blast', label: 'Email Blast' },
    { id: 'settings', label: 'Settings' },
  ];

  return (
    <div className="space-y-6">
      {/* Section Nav */}
      <div className="flex gap-2 flex-wrap">
        {sections.map(sec => (
          <Button key={sec.id} variant={activeSection === sec.id ? 'default' : 'outline'} size="sm" onClick={() => {
            setActiveSection(sec.id);
            if (sec.id === 'referrals') fetchReferrals(statusFilter);
            if (sec.id === 'campaigns') fetchCampaigns();
            if (sec.id === 'name-referrals') fetchNameReferrals();
          }} data-testid={`referral-section-${sec.id}`}>
            {sec.label}
          </Button>
        ))}
        <Button variant="ghost" size="sm" onClick={fetchAnalytics} className="ml-auto"><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
      </div>

      {/* ═══ Overview ═══ */}
      {activeSection === 'overview' && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border p-4"><div className="text-sm text-gray-500">Total Clicks</div><div className="text-2xl font-bold text-[#0A2342]">{kpis.total_clicks}</div></div>
            <div className="bg-white rounded-lg border p-4"><div className="text-sm text-gray-500">Total Signups</div><div className="text-2xl font-bold text-green-600">{kpis.total_signups}</div></div>
            <div className="bg-white rounded-lg border p-4"><div className="text-sm text-gray-500">Total Completed</div><div className="text-2xl font-bold text-[#FF6B35]">{kpis.total_completed}</div></div>
            <div className="bg-white rounded-lg border p-4"><div className="text-sm text-gray-500">Rewards Issued</div><div className="text-2xl font-bold text-purple-600">{kpis.rewards_issued}</div></div>
          </div>

          <div className="bg-white rounded-lg border p-6">
            <h3 className="font-semibold text-[#0A2342] mb-4">Conversion Funnel</h3>
            <div className="flex items-center justify-between gap-2">
              {[
                { label: 'Clicks', value: kpis.total_clicks, color: 'bg-blue-500' },
                { label: 'Signups', value: kpis.total_signups, rate: funnel.click_to_signup, color: 'bg-green-500' },
                { label: 'Booked', value: kpis.total_booked, rate: funnel.signup_to_booked, color: 'bg-yellow-500' },
                { label: 'Completed', value: kpis.total_completed, rate: funnel.booked_to_completed, color: 'bg-[#FF6B35]' },
              ].map((step, idx, arr) => (
                <div key={step.label} className="flex-1 text-center">
                  <div className={`${step.color} text-white rounded-lg p-4 mb-2`}>
                    <div className="text-2xl font-bold">{step.value}</div>
                    <div className="text-xs opacity-80">{step.label}</div>
                  </div>
                  {step.rate !== undefined && <div className="text-xs text-gray-500">{step.rate}% conv.</div>}
                  {idx < arr.length - 1 && <div className="text-gray-300 mt-1"><ArrowRight className="w-4 h-4 mx-auto" /></div>}
                </div>
              ))}
            </div>
            <div className="text-center mt-4 text-sm text-gray-600">
              Overall: <strong className="text-[#FF6B35]">{funnel.overall_conversion}%</strong>
            </div>
          </div>

          {analytics?.sources && Object.keys(analytics.sources).length > 0 && (
            <div className="bg-white rounded-lg border p-6">
              <h3 className="font-semibold text-[#0A2342] mb-4">Source Breakdown</h3>
              <table className="w-full text-sm">
                <thead className="bg-gray-50"><tr><th className="text-left p-3">Source</th><th className="text-right p-3">Clicks</th><th className="text-right p-3">Signups</th><th className="text-right p-3">Completed</th><th className="text-right p-3">Conv.</th></tr></thead>
                <tbody>
                  {Object.entries(analytics.sources).map(([source, data]) => (
                    <tr key={source} className="border-t"><td className="p-3 font-medium capitalize">{source}</td><td className="p-3 text-right">{data.clicks}</td><td className="p-3 text-right">{data.signups}</td><td className="p-3 text-right">{data.completed}</td><td className="p-3 text-right">{data.clicks > 0 ? Math.round(data.completed / data.clicks * 100) : 0}%</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {analytics?.daily?.length > 0 && (
            <div className="bg-white rounded-lg border p-6">
              <h3 className="font-semibold text-[#0A2342] mb-4">Daily Referrals (Last 14 Days)</h3>
              <div className="flex items-end gap-1 h-32">
                {analytics.daily.slice(-14).map((day, idx) => {
                  const max = Math.max(...analytics.daily.slice(-14).map(d => d.clicks), 1);
                  const h = (day.clicks / max) * 100;
                  return (<div key={idx} className="flex-1 flex flex-col items-center"><div className="w-full bg-gray-100 rounded-t relative" style={{ height: '100px' }}><div className="absolute bottom-0 w-full bg-[#0A2342] rounded-t" style={{ height: `${h}%` }} /></div><span className="text-[10px] text-gray-400 mt-1">{day.date.slice(5)}</span></div>);
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══ Campaigns ═══ */}
      {activeSection === 'campaigns' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="font-semibold text-[#0A2342] text-lg">Campaign Codes</h3>
            <Button onClick={() => setShowNewCampaign(true)} data-testid="create-campaign-btn">
              <Plus className="w-4 h-4 mr-2" /> New Campaign
            </Button>
          </div>

          {/* New Campaign Form */}
          {showNewCampaign && (
            <div className="bg-white rounded-lg border p-6">
              <h4 className="font-medium text-[#0A2342] mb-4">Create Campaign Code</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code *</label>
                  <Input placeholder="e.g. SUMMER-DIVE, INSTA-2026" value={newCampaign.code} onChange={(e) => setNewCampaign({ ...newCampaign, code: e.target.value.toUpperCase().replace(/\s+/g, '-') })} data-testid="campaign-code-input" />
                  <p className="text-xs text-gray-400 mt-1">This is what users will enter or see in links</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name</label>
                  <Input placeholder="e.g. Summer Instagram Campaign" value={newCampaign.name} onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                  <Select value={newCampaign.source} onValueChange={(v) => setNewCampaign({ ...newCampaign, source: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="facebook">Facebook</SelectItem>
                      <SelectItem value="instagram">Instagram</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp</SelectItem>
                      <SelectItem value="tiktok">TikTok</SelectItem>
                      <SelectItem value="partner">Partner</SelectItem>
                      <SelectItem value="social">Social (General)</SelectItem>
                      <SelectItem value="offline">Offline / Print</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <Input placeholder="Brief description of the campaign" value={newCampaign.description} onChange={(e) => setNewCampaign({ ...newCampaign, description: e.target.value })} />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={createCampaign} disabled={creatingCampaign} className="bg-[#FF6B35] hover:bg-[#FF6B35]/90">
                  {creatingCampaign ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create Campaign
                </Button>
                <Button variant="outline" onClick={() => setShowNewCampaign(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Campaign Links Modal */}
          {campaignLinks && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="flex justify-between items-start mb-4">
                <h4 className="font-medium text-green-800">Campaign Created! Here are your links:</h4>
                <button onClick={() => setCampaignLinks(null)} className="text-gray-500 hover:text-gray-700"><X className="w-4 h-4" /></button>
              </div>
              <div className="space-y-2">
                {Object.entries(campaignLinks.links).map(([platform, url]) => (
                  <div key={platform} className="flex items-center gap-2 bg-white rounded-lg p-3 border">
                    <Badge variant="outline" className="capitalize w-24 justify-center">{platform}</Badge>
                    <span className="flex-1 text-sm font-mono text-gray-600 truncate">{url}</span>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(url)}><Copy className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Campaign List */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3">Code</th>
                  <th className="text-left p-3">Name</th>
                  <th className="text-left p-3">Source</th>
                  <th className="text-right p-3">Clicks</th>
                  <th className="text-right p-3">Signups</th>
                  <th className="text-right p-3">Completed</th>
                  <th className="text-center p-3">Status</th>
                  <th className="text-center p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.length === 0 ? (
                  <tr><td colSpan="8" className="p-8 text-center text-gray-500">No campaign codes yet. Create one to start tracking your social campaigns!</td></tr>
                ) : campaigns.map(c => (
                  <tr key={c.id} className="border-t hover:bg-gray-50">
                    <td className="p-3 font-mono font-bold text-[#FF6B35]">{c.code}</td>
                    <td className="p-3">{c.name}</td>
                    <td className="p-3"><Badge variant="outline" className="capitalize">{c.source}</Badge></td>
                    <td className="p-3 text-right">{c.stats?.clicks || 0}</td>
                    <td className="p-3 text-right">{c.stats?.signups || 0}</td>
                    <td className="p-3 text-right font-semibold text-green-600">{c.stats?.completed || 0}</td>
                    <td className="p-3 text-center">
                      <Badge className={c.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                        {c.is_active ? 'Active' : 'Disabled'}
                      </Badge>
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex justify-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => {
                          const origin = window.location.origin;
                          const base = `${origin}/join?ref=${c.code}`;
                          setCampaignLinks({
                            links: {
                              direct: base,
                              facebook: `${base}&utm_source=facebook`,
                              instagram: `${base}&utm_source=instagram`,
                              email: `${base}&utm_source=email`,
                              whatsapp: `${base}&utm_source=whatsapp`,
                              tiktok: `${base}&utm_source=tiktok`,
                            }
                          });
                        }} title="Get Links"><Link className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(`${window.location.origin}/join?ref=${c.code}`)} title="Copy Direct Link"><Copy className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteCampaign(c.id)} title="Delete"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ Referrals List ═══ */}
      {activeSection === 'referrals' && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="p-4 border-b flex items-center gap-4">
            <h3 className="font-semibold text-[#0A2342]">All Referrals</h3>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); fetchReferrals(v); }}>
              <SelectTrigger className="w-40"><SelectValue placeholder="All Statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All</SelectItem>
                <SelectItem value="CLICKED">Clicked</SelectItem>
                <SelectItem value="SIGNED_UP">Signed Up</SelectItem>
                <SelectItem value="BOOKED">Booked</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="INVALID">Invalid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr><th className="text-left p-3">Referrer</th><th className="text-left p-3">Referred</th><th className="text-left p-3">Status</th><th className="text-left p-3">Source</th><th className="text-left p-3">Date</th><th className="text-center p-3">Actions</th></tr>
              </thead>
              <tbody>
                {referrals.length === 0 ? (
                  <tr><td colSpan="6" className="p-8 text-center text-gray-500">No referrals found</td></tr>
                ) : referrals.map(ref => (
                  <tr key={ref.id} className="border-t hover:bg-gray-50">
                    <td className="p-3"><div className="font-medium">{ref.referrer_name || 'Campaign'}</div><div className="text-xs text-gray-500">{ref.referrer_email || ref.referral_code}</div></td>
                    <td className="p-3"><div className="font-medium">{ref.referred_name}</div><div className="text-xs text-gray-500">{ref.referred_email}</div></td>
                    <td className="p-3"><Badge className={ref.status === 'COMPLETED' ? 'bg-green-100 text-green-700' : ref.status === 'SIGNED_UP' ? 'bg-blue-100 text-blue-700' : ref.status === 'BOOKED' ? 'bg-yellow-100 text-yellow-700' : ref.status === 'INVALID' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}>{ref.status}</Badge></td>
                    <td className="p-3 capitalize text-gray-600">{ref.source}</td>
                    <td className="p-3 text-gray-500 text-xs">{ref.created_at ? new Date(ref.created_at).toLocaleDateString() : '-'}</td>
                    <td className="p-3 text-center">
                      {ref.status !== 'INVALID' && ref.status !== 'COMPLETED' && (
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => invalidateReferral(ref.id)} title="Invalidate"><X className="w-4 h-4" /></Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ Top Referrers ═══ */}
      {activeSection === 'top-referrers' && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="p-4 border-b"><h3 className="font-semibold text-[#0A2342]">Top Referrers</h3></div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr><th className="text-left p-3">#</th><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-right p-3">Total</th><th className="text-right p-3">Signups</th><th className="text-right p-3">Completed</th><th className="text-right p-3">Rewards</th></tr></thead>
            <tbody>
              {(analytics?.top_referrers || []).length === 0 ? (
                <tr><td colSpan="7" className="p-8 text-center text-gray-500">No referrers yet</td></tr>
              ) : (analytics?.top_referrers || []).map((ref, idx) => (
                <tr key={ref.id} className="border-t hover:bg-gray-50"><td className="p-3 font-bold text-[#FF6B35]">{idx + 1}</td><td className="p-3 font-medium">{ref.name}</td><td className="p-3 text-gray-600">{ref.email}</td><td className="p-3 text-right">{ref.total_referrals}</td><td className="p-3 text-right">{ref.signups}</td><td className="p-3 text-right font-semibold text-green-600">{ref.completed}</td><td className="p-3 text-right">{ref.rewards_count}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══ Name Referrals (manual) ═══ */}
      {activeSection === 'name-referrals' && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="p-4 border-b">
            <h3 className="font-semibold text-[#0A2342]">Referred by Name (No Code)</h3>
            <p className="text-sm text-gray-500 mt-1">Users who entered a person's name instead of a referral code during signup</p>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr><th className="text-left p-3">User</th><th className="text-left p-3">Email</th><th className="text-left p-3">Referred By (Name)</th><th className="text-left p-3">Has Code?</th><th className="text-left p-3">Joined</th></tr></thead>
            <tbody>
              {nameReferrals.length === 0 ? (
                <tr><td colSpan="5" className="p-8 text-center text-gray-500">No name-only referrals yet</td></tr>
              ) : nameReferrals.map(u => (
                <tr key={u.id} className="border-t hover:bg-gray-50">
                  <td className="p-3 font-medium">{u.first_name} {u.last_name}</td>
                  <td className="p-3 text-gray-600">{u.email}</td>
                  <td className="p-3 font-semibold text-[#FF6B35]">{u.referred_by_name}</td>
                  <td className="p-3">{u.referred_by ? <Badge className="bg-green-100 text-green-700">Yes</Badge> : <Badge variant="outline">No</Badge>}</td>
                  <td className="p-3 text-gray-500 text-xs">{u.date_joined || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══ Fraud ═══ */}
      {activeSection === 'fraud' && (
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="p-4 border-b flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            <h3 className="font-semibold text-[#0A2342]">Fraud Detection ({fraud?.total || 0} flags)</h3>
          </div>
          {fraud?.flags?.length === 0 ? (
            <div className="p-8 text-center text-gray-500"><Check className="w-8 h-8 mx-auto mb-2 text-green-500" /><p>No suspicious activity detected</p></div>
          ) : (
            <div className="divide-y">
              {(fraud?.flags || []).map((flag, idx) => (
                <div key={idx} className="p-4 hover:bg-gray-50 flex items-center gap-3">
                  <Badge className={flag.severity === 'high' ? 'bg-red-100 text-red-700' : flag.severity === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}>{flag.severity}</Badge>
                  <Badge variant="outline">{flag.type}</Badge>
                  <span className="text-sm">{flag.detail}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ Email Blast ═══ */}
      {activeSection === 'email-blast' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border p-6">
            <h3 className="font-semibold text-[#0A2342] mb-2 flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-[#FF6B35]" /> Launch Referral Program Email
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Send a branded email to users announcing the referral program. Each user will receive their personal referral link and reward details.
            </p>
            <div className="flex items-center gap-4 mb-4">
              <label className="text-sm font-medium text-gray-700">Send to:</label>
              <Select value={blastRecipientType} onValueChange={setBlastRecipientType}>
                <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="members_only">Members Only</SelectItem>
                  <SelectItem value="non_members">Non-Members Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm">
              <h4 className="font-medium mb-2">Email Preview:</h4>
              <ul className="list-disc list-inside space-y-1 text-gray-600">
                <li>Subject: <em>"Refer a Friend & Earn FREE Diving Rewards!"</em></li>
                <li>Contains: How it works (3 steps), personal referral link, reward tiers</li>
                <li>CTA: "View Your Referral Dashboard"</li>
                <li>Each user gets their unique link auto-generated</li>
              </ul>
            </div>
            <Button onClick={sendReferralBlast} disabled={sendingBlast} className="bg-[#FF6B35] hover:bg-[#FF6B35]/90" data-testid="send-referral-blast-btn">
              {sendingBlast ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</> : <><Send className="w-4 h-4 mr-2" />Send Referral Program Email</>}
            </Button>
          </div>
        </div>
      )}

      {/* ═══ Settings ═══ */}
      {activeSection === 'settings' && (
        <div className="bg-white rounded-lg border p-6 max-w-2xl">
          <h3 className="font-semibold text-[#0A2342] mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5" /> Campaign Settings
          </h3>
          <div className="space-y-4">
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <input type="checkbox" checked={configForm.enabled} onChange={(e) => setConfigForm({ ...configForm, enabled: e.target.checked })} className="w-5 h-5" />
              <div><span className="font-medium">Campaign Enabled</span><p className="text-xs text-gray-500">When disabled, new referral links won't be tracked</p></div>
            </label>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reward Expiry (days)</label>
              <Input type="number" min="7" max="365" value={configForm.reward_expiry_days} onChange={(e) => setConfigForm({ ...configForm, reward_expiry_days: parseInt(e.target.value) || 30 })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Referral Cookie Expiry (days)</label>
              <Input type="number" min="1" max="90" value={configForm.cookie_expiry_days} onChange={(e) => setConfigForm({ ...configForm, cookie_expiry_days: parseInt(e.target.value) || 30 })} />
            </div>
            <div className="border-t pt-4">
              <h4 className="font-medium text-[#0A2342] mb-3">Reward Tiers</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between p-3 bg-blue-50 rounded-lg"><span>1 Completed Referral</span><strong>Friend gets Free Discover Scuba</strong></div>
                <div className="flex justify-between p-3 bg-green-50 rounded-lg"><span>3 Completed Referrals</span><strong>Referrer gets Free Refresher Course</strong></div>
                <div className="flex justify-between p-3 bg-orange-50 rounded-lg"><span>5 Completed Referrals</span><strong>Referrer gets R1,000 OFF</strong></div>
              </div>
            </div>
            <Button onClick={saveConfig} disabled={savingConfig} className="bg-[#FF6B35] hover:bg-[#FF6B35]/90">
              {savingConfig ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}Save Settings
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralsTab;
